import React, { useEffect, useRef, useState } from "react";
import { ReactComponent as ChevronLeft } from "../../assets/svgs/sf-symbols/ios/chevron.left.svg";
import { ReactComponent as SearchIcon } from "../../assets/svgs/sf-symbols/notes/magnifyingglass.svg";
import { ReactComponent as MicIcon } from "../../assets/svgs/sf-symbols/ios/microphone.fill.svg";

// Building blocks shared by the iPhone's apps (Notes, Phone, Messages) and Search.

const SLIDE_MS = 420; // how long a screen takes to slide in or out

// An open app: it zooms out of its icon (`origin`, the icon's spot on screen) and back into it when
// closed. The bar at the bottom (tap it, or swipe up from it) goes back to the Home Screen.
export const AppShell = ({ label, origin, className = "", onClose, children }) => {
  const [closing, setClosing] = useState(false);
  const swipe = useRef(null);

  const close = () => {
    if (closing) return;
    setClosing(true);
    setTimeout(onClose, 280);
  };

  return (
    <div
      className={`ios-app ${className} ${closing ? "is-closing" : ""}`}
      style={{ transformOrigin: origin }}
      role="dialog"
      aria-label={label}
    >
      {children}
      <button
        type="button"
        className="ios-home-indicator"
        aria-label="Go to Home Screen"
        onClick={close}
        onPointerDown={(e) => (swipe.current = e.clientY)}
        onPointerMove={(e) => swipe.current !== null && swipe.current - e.clientY > 24 && close()}
        onPointerUp={() => (swipe.current = null)}
        onPointerCancel={() => (swipe.current = null)}
      />
    </div>
  );
};

// One screen: a large title that shrinks into the top bar as the content scrolls under it, the
// top bar's buttons (`left`, `right`, and `center` in place of the title), and an optional bottom
// toolbar. `inlineTitle` keeps the title small in the top bar from the start; `overlay` stays put
// over the content as it scrolls (the contacts' A–Z index).
export const Screen = ({ title, subtitle, inlineTitle, left, center, right, bottom, overlay, children, scrollTestId }) => {
  const [scrolled, setScrolled] = useState(false);
  const large = title && !inlineTitle;
  return (
    <>
      <div
        className="ios-app-scroll"
        data-testid={scrollTestId}
        onScroll={(e) => setScrolled(e.currentTarget.scrollTop > 40)}
      >
        {large && (
          <header className="ios-large-title">
            <h1>{title}</h1>
            {subtitle && <p>{subtitle}</p>}
          </header>
        )}
        {children}
      </div>

      <div className="ios-edge ios-edge--top" />
      <div className="ios-edge ios-edge--bottom" />

      <div className="ios-nav">
        {left || <span />}
        {center ||
          (title && (
            <span className={`ios-inline-title ${scrolled || inlineTitle ? "is-shown" : ""}`} aria-hidden={!inlineTitle}>
              {title}
            </span>
          ))}
        {right || <span />}
      </div>
      {bottom && <div className="ios-toolbar">{bottom}</div>}
      {overlay}
    </>
  );
};

export const BackButton = ({ onClick }) => (
  <button type="button" onClick={onClick} className="ios-circle glass glass--light" aria-label="Back">
    <ChevronLeft className="ios-sym-back" />
  </button>
);

// A button that's only there to look the part.
export const Decor = ({ children, className = "ios-circle" }) => (
  <span className={`${className} glass glass--light`} aria-hidden="true">
    {children}
  </span>
);

// A search field with the magnifier and the dictation mic. `className` styles it (Notes' glass
// field in the bottom toolbar, the Phone app's gray bar under the title).
export const SearchField = ({ value, onChange, label, className }) => (
  <label className={className}>
    <SearchIcon className="ios-sym-search" aria-hidden="true" />
    <input
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Search"
      aria-label={label}
      autoComplete="off"
    />
    <MicIcon className="ios-sym-mic" aria-hidden="true" />
  </label>
);

// A navigation stack, as in UINavigationController: `push` slides a screen in over the current
// one, `back` slides it away, `replaceTop` swaps the top screen in place (it fades in), and `reset`
// starts over. Render `pages`, giving each page's element `key={entry.key}` and `pageProps(entry)`.
export const useNavStack = (initial) => {
  const [stack, setStack] = useState(() => initial.map((entry, key) => ({ ...entry, key })));
  const [leaving, setLeaving] = useState(null); // the screen sliding away after Back
  const nextKey = useRef(initial.length);
  // The screen that just arrived and animates in; a screen uncovered by Back just slides back.
  const fresh = useRef(null);

  const push = (entry) => {
    if (leaving) return;
    const key = nextKey.current++;
    fresh.current = key;
    setStack((s) => [...s, { ...entry, key, enter: "push" }]);
  };

  const back = () => {
    if (leaving || stack.length < 2) return;
    fresh.current = null;
    setLeaving(stack[stack.length - 1]);
    setStack((s) => s.slice(0, -1));
    setTimeout(() => setLeaving(null), SLIDE_MS);
  };

  const replaceTop = (entry) => {
    const key = nextKey.current++;
    fresh.current = key;
    setStack((s) => [...s.slice(0, -1), { ...entry, key, enter: "fade" }]);
  };

  const reset = (entries) => {
    fresh.current = null;
    setLeaving(null);
    setStack(entries.map((entry) => ({ ...entry, key: nextKey.current++ })));
  };

  const pageProps = (entry, className = "") => {
    const isLeaving = entry === leaving;
    const depth = stack.length - 1 - stack.indexOf(entry); // 0 = the screen on top
    const enter = entry.key === fresh.current ? `is-enter-${entry.enter}` : "";
    const state = isLeaving ? "is-leaving" : depth > 0 ? "is-under" : `is-top ${enter}`;
    const hidden = depth > 0 || isLeaving;
    return {
      className: `ios-page ${className} ${state}`,
      "aria-hidden": hidden,
      inert: hidden ? "" : undefined,
    };
  };

  return { stack, pages: leaving ? [...stack, leaving] : stack, push, back, replaceTop, reset, pageProps };
};

// How far the on-screen keyboard reaches up the screen, so the search field can sit on top of it.
export const useKeyboardInset = (active) => {
  const [inset, setInset] = useState(0);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!active || !vv) return undefined;
    const update = () => setInset(Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop)));
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
      setInset(0);
    };
  }, [active]);
  return inset;
};
